import clsx from 'clsx'
import PropTypes from 'prop-types'
import React from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import FieldHelpIcon from '../FieldHelpIcon'
import { PW_MANAGER_OFF } from '../../util/inputAttrs'

const propTypes = {
  /**
   * id of the text field
   */
  id: PropTypes.string.isRequired,
  /**
   * Name of the text field
   * If no name is passed id is used as name
   */
  name: PropTypes.string,
  /**
   * Title of the field. Must be a FormattedMessage id
   */
  title: PropTypes.string,
  /**
   * Callback called when an option is selected
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Value of the field
   */
  value: PropTypes.string,
  /**
   * validation errors messages
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * Class of the container wrapper
   */
  containerClassName: PropTypes.string,
  /**
   * Class to style the title
   */
  titleClassName: PropTypes.string,
  /**
   * Class to override default control styles
   */
  className: PropTypes.string,
  /**
   * Indicates if field is required
   */
  required: PropTypes.bool,
  /**
   * Input placeholder
   */
  placeholder: PropTypes.string,
  /**
   * If true, displays label and input side-by-side (like PropertyField)
   */
  inline: PropTypes.bool,
  /**
   * If true, adds data attributes so password managers (LastPass, 1Password, etc.) ignore this field
   */
  disablePasswordManager: PropTypes.bool,
  /** Optional `FormattedMessage` id for a help tooltip next to the title */
  titleTooltipId: PropTypes.string,
}

function TextField(props) {
  const {
    id,
    name = null,
    title = null,
    titleTooltipId = null,
    placeholder = '',
    onChange,
    value = null,
    errors = [],
    containerClassName = '',
    titleClassName = '',
    className = '',
    required = false,
    inline = false,
    disablePasswordManager = false,
    ...rest
  } = props ?? {}

  const inputProps = disablePasswordManager ? { ...PW_MANAGER_OFF, ...rest } : rest
  const hasError = errors?.length > 0

  if (inline) {
    const { style: restStyle, ...restWithoutStyle } = inputProps
    return (
      <Row className={clsx('mb-2 align-items-center', containerClassName)}>
        {title && (
          <Col sm="4" className={clsx('text-bold text-muted', titleClassName)}>
            <FormattedMessage id={title} />
            {titleTooltipId && <FieldHelpIcon tooltipId={titleTooltipId} />}
            {required && <span className="font-size-regular text-color-danger">*</span>}
          </Col>
        )}
        <Col sm={title ? '8' : '12'} style={{ marginLeft: title ? '-4px' : '0' }}>
          <Form.Control
            {...restWithoutStyle}
            id={id}
            name={name || id}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            isInvalid={hasError}
            className={clsx(hasError && 'error-border', className)}
            style={restStyle}
          />

          {hasError &&
            errors.map((errorMessage) => (
              <Form.Control.Feedback key={errorMessage} type="invalid" className="d-block">
                {errorMessage}
              </Form.Control.Feedback>
            ))}
        </Col>
      </Row>
    )
  }

  return (
    <div className={clsx('mb-3', containerClassName)}>
      {title && (
        <p className={clsx('mb-2', titleClassName)}>
          <FormattedMessage id={title} />
          {titleTooltipId && <FieldHelpIcon tooltipId={titleTooltipId} />}
          {required && <span className="font-size-regular text-color-danger">*</span>}
        </p>
      )}

      <Form.Control
        {...inputProps}
        id={id}
        name={name || id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isInvalid={hasError}
        className={clsx(hasError && 'error-border', className)}
      />

      {hasError &&
        errors.map((errorMessage) => (
          <Form.Control.Feedback key={errorMessage} type="invalid" className="d-block">
            {errorMessage}
          </Form.Control.Feedback>
        ))}
    </div>
  )
}

TextField.propTypes = propTypes

export default TextField
